import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import React, {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router";
import ClientConfigurationsTable from "../components/ClientConfiguration/ClientConfigurationsTable";
import {ClientConfiguration} from "../models/ClientConfiguration/ClientConfiguration";
import { UserRole } from "../models/User";
import ClientConfigurationsRepository from "../repositories/ClientConfigurationsRepository";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../components/common/ActionConfirmationModal";
import DeleteClientConfigurationModal from "../components/ClientConfiguration/DeleteClientConfigurationModal";
import { toast } from "sonner";
import { QUERY_KEYS } from "../constants/QueryKeys";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";
import { useRequiresReason } from "../hooks/useRequiresReason";

interface ClientConfigurationsPageProps {
  organizationId?: string;
  userRole: UserRole;
  isAssignedUser: boolean
}

const ClientConfigurationsPage: React.FC<ClientConfigurationsPageProps> = ({
  organizationId,
  userRole,
  isAssignedUser
}) => {
  const requiresReason = useRequiresReason();
  const configurationsRepository = new ClientConfigurationsRepository();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [configs, setConfigs] = React.useState<ClientConfiguration[]>([]);
  const [configToDelete, setConfigToDelete] = useState<ClientConfiguration | null>(null);
  const [defaultConfigId, setDefaultConfigId] = useState<string | null>(null);

  const configurationsQuery = useQuery({
    queryKey: [QUERY_KEYS.CLIENT_CONFIGURATIONS, organizationId],
    queryFn: async () => {
      return await configurationsRepository.getClientConfigurations(organizationId || "");
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (configurationsQuery.data)
      setConfigs(configurationsQuery.data);
  }, [organizationId, configurationsQuery.data]);

  const { isLoading, isError } = configurationsQuery;

  const handleConfirmDelete = async (replacementConfigId: string | null, newDefaultConfigId: string | null) => {
    if (configToDelete?.id) {
      const name = configToDelete.name;
      try {
        await configurationsRepository.deleteConfig(configToDelete.id, replacementConfigId, newDefaultConfigId);
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_CONFIGURATIONS, organizationId] });
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] });
        // The versioning survey reports one entry per client configuration, so a deleted one lingers in
        // it — and a replacement configuration resolves its own drive names, which the survey has not
        // seen yet.
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.ORGANIZATION_GRAPH_LIBRARY_VERSIONING, organizationId],
        });
        toast.success(`Configuration "${name}" deleted`);
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to delete configuration");
      }
    }
    setConfigToDelete(null);
  };

  /**
   * `defaultConfigId` is captured at call time and reset to null inside; we
   * `await` so ActionConfirmationModal can surface success/error toasts via
   * `ACTION_CONFIGS.SET_CONFIGURATION_AS_DEFAULT.successMessage`. The direct
   * (non-modal) Admin path wraps this with its own toast below.
   */
  const handleSetAsDefaultConfig = async (reason?: string) => {
    if (!defaultConfigId) return;
    const id = defaultConfigId;
    setDefaultConfigId(null);
    await configurationsRepository.setDefaultConfig(id, reason);
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_CONFIGURATIONS, organizationId] });
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, organizationId] });
    // The survey marks which configuration clients fall back to, because a versioning problem there
    // reaches every client with none assigned. Left stale, it keeps pointing at the previous default.
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.ORGANIZATION_GRAPH_LIBRARY_VERSIONING, organizationId],
    });
  };

  const handleSetAsDefaultConfigDirect = async () => {
    if (!defaultConfigId) return;
    try {
      await handleSetAsDefaultConfig();
      toast.success("Default configuration set");
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to set default configuration");
    }
  };

  const handleSetDefaultConfig = (id: string) => {
    setDefaultConfigId(id);
    setConfigs((prevConfigs) =>
      prevConfigs.map((config) => ({
        ...config,
        isDefault: config.id === id,
      }))
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Client Configurations<DocsHelpLink to="configuration/client-configuration" /></h2>
        <div className="flex flex-row gap-2">
          {isLoading || isError ? (
            <Skeleton className="h-8 w-[120px]" />
          ) : (
            <>
              {requiresReason ? (
                <ActionConfirmationModal
                  actionConfig={ACTION_CONFIGS.SET_CONFIGURATION_AS_DEFAULT}
                  onConfirm={async (reason) => await handleSetAsDefaultConfig(reason)}
                >
                  <Button
                    size="sm"
                    disabled={defaultConfigId === null}
                  >
                    Save Changes
                  </Button>
                </ActionConfirmationModal>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button
                          size="sm"
                          disabled={defaultConfigId === null || userRole === UserRole.Viewer}
                          onClick={handleSetAsDefaultConfigDirect}
                        >
                          Save Changes
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {(defaultConfigId === null || userRole === UserRole.Viewer) && (
                      <TooltipContent>Make changes to enable saving</TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </>
          )}
          {isLoading || isError ? (
            <Skeleton className="h-8 w-[160px]" />
          ) : (
            <Button
              size="sm"
              disabled={userRole === UserRole.Viewer}
              onClick={() =>
                navigate(`/organization/${organizationId}/client-configuration/create`)
              }
            >
              Create Configuration
            </Button>
          )}
        </div>
      </div>
      {isLoading || isError ? (
        <Skeleton className="h-[200px] w-full" />
      ) : (
        <ClientConfigurationsTable
          userRole={userRole}
          isAssignedUser={isAssignedUser}
          organizationId={organizationId ?? ""}
          configurations={configs}
          setDefaultConfigId={handleSetDefaultConfig}
          onDeleteConfig={(config) => setConfigToDelete(config)}
        />
      )}
      <DeleteClientConfigurationModal
        open={configToDelete !== null}
        config={configToDelete}
        organizationId={organizationId ?? ""}
        onClose={() => setConfigToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ClientConfigurationsPage;
